import { FC, useEffect, useRef, useState } from "react"
import { View, ViewStyle, TextStyle, Platform, Dimensions } from "react-native"
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSequence,
  withSpring,
  interpolate,
  runOnJS,
  useAnimatedProps,
  Easing,
} from "react-native-reanimated"
import Svg, { Circle } from "react-native-svg"

import liriaAnimation from "@assets/animations/Liria_Scan_opt.json"

import { LottieView } from "@/components/LottieView.web"
import { Text } from "@/components/Text"
import { colors, typography, spacing } from "@/theme"

// Import Lottie animation

const AnimatedCircle = Animated.createAnimatedComponent(Circle)

interface AnimatedCountdownTimerProps {
  duration?: number
  onComplete?: () => void
  style?: ViewStyle
  showDetailedMessage?: boolean
  /** Render at ~70% size to fit alongside survey card on mobile */
  compact?: boolean
}

const TIMER_SIZE = 260 // 30% larger (200 * 1.3)
const RADIUS = 110 // Scaled proportionally
const STROKE_WIDTH = 5 // Slightly thicker for larger size
const CIRCUMFERENCE = 2 * Math.PI * RADIUS
const LOTTIE_SIZE = 220 // Scaled proportionally (130 * 1.3)
const COUNTDOWN_FONT_SIZE = 22

export const AnimatedCountdownTimer: FC<AnimatedCountdownTimerProps> = ({
  duration = 39,
  onComplete,
  style,
  showDetailedMessage = false,
  compact = false,
}) => {
  const onCompleteRef = useRef(onComplete)
  onCompleteRef.current = onComplete

  const [currentCount, setCurrentCount] = useState(duration)
  const [showEmailMessage, setShowEmailMessage] = useState(false)
  const [dotCount, setDotCount] = useState(0)

  // Animated values
  const progress = useSharedValue(1)
  const compactScale = useSharedValue(1)
  const opacity = useSharedValue(0)
  const numberScale = useSharedValue(1)
  const messageOpacity = useSharedValue(0)
  const animationOpacity = useSharedValue(1)

  // Fade in on mount
  useEffect(() => {
    opacity.value = withTiming(1, { duration: 300 })
  }, [])

  // Animate scale when compact prop changes — shrink more on mobile
  const isMobile = Dimensions.get("window").width < 768
  useEffect(() => {
    const targetScale = compact ? (isMobile ? 0.4 : 0.6) : 1
    compactScale.value = withTiming(targetScale, {
      duration: 400,
      easing: Easing.out(Easing.cubic),
    })
  }, [compact, isMobile])

  // Compact scale animated style
  const compactAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: compactScale.value }],
    marginBottom: compactScale.value < 1 ? -80 * (1 - compactScale.value) : 0,
  }))

  // Countdown logic
  useEffect(() => {
    let countdownValue = duration

    const interval = setInterval(() => {
      countdownValue -= 1

      if (countdownValue >= 0) {
        // Update progress smoothly
        progress.value = withTiming(countdownValue / duration, {
          duration: 900,
          easing: Easing.linear,
        })

        // Pulse animation on each second
        numberScale.value = withSequence(
          withSpring(1.15, { damping: 5, stiffness: 200 }),
          withSpring(1, { damping: 5, stiffness: 200 }),
        )

        // Update count
        runOnJS(setCurrentCount)(countdownValue)

        // Special effects for last 5 seconds
        if (countdownValue <= 5 && countdownValue > 0) {
          // More dramatic pulse for final seconds
          numberScale.value = withSequence(
            withSpring(1.2, { damping: 3, stiffness: 300 }),
            withSpring(1, { damping: 3, stiffness: 300 }),
          )
        }
      }

      if (countdownValue === 0) {
        clearInterval(interval)
        // Transition to email message (keep animation running)
        setTimeout(() => {
          runOnJS(setShowEmailMessage)(true)
          messageOpacity.value = withTiming(1, { duration: 400 })
          if (onCompleteRef.current) {
            runOnJS(onCompleteRef.current)()
          }
        }, 500)
      }
    }, 1000)

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration])

  // Animated dots for email message
  useEffect(() => {
    if (!showEmailMessage) return

    const dotsInterval = setInterval(() => {
      setDotCount((prev) => (prev + 1) % 4)
    }, 500)

    return () => clearInterval(dotsInterval)
  }, [showEmailMessage])

  // Container animation style
  const containerAnimatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
  }))

  // Lottie animation style
  const _lottieAnimatedStyle = useAnimatedStyle(() => ({
    opacity: animationOpacity.value,
  }))

  // Number animation style
  const numberAnimatedStyle = useAnimatedStyle(() => ({
    transform: [{ scale: numberScale.value }],
    opacity: interpolate(messageOpacity.value, [0, 1], [1, 0]),
  }))

  // Message animation style
  const messageAnimatedStyle = useAnimatedStyle(() => ({
    opacity: messageOpacity.value,
  }))

  // Detailed message card animation style (simple fade-in, no scale)
  const detailedMessageAnimatedStyle = useAnimatedStyle(() => ({
    opacity: messageOpacity.value,
  }))

  // SVG circle animation
  const circleAnimatedProps = useAnimatedProps(() => ({
    strokeDashoffset: CIRCUMFERENCE * (1 - progress.value),
  }))

  const dots = ".".repeat(dotCount)

  return (
    <Animated.View style={[styles.container, style, compactAnimatedStyle]}>
      <Animated.View style={[styles.timerCircle, containerAnimatedStyle]}>
        {/* SVG Progress Ring */}
        <Svg
          width={TIMER_SIZE}
          height={TIMER_SIZE}
          viewBox={`0 0 ${TIMER_SIZE} ${TIMER_SIZE}`}
          style={styles.svg}
        >
          {/* Background circle */}
          <Circle
            cx={TIMER_SIZE / 2}
            cy={TIMER_SIZE / 2}
            r={RADIUS}
            stroke={colors.palette.neutral200}
            strokeWidth={STROKE_WIDTH}
            fill="none"
          />

          {/* Animated progress circle */}
          <AnimatedCircle
            cx={TIMER_SIZE / 2}
            cy={TIMER_SIZE / 2}
            r={RADIUS}
            stroke={colors.primary}
            strokeWidth={STROKE_WIDTH}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            animatedProps={circleAnimatedProps}
            transform={`rotate(-90 ${TIMER_SIZE / 2} ${TIMER_SIZE / 2})`}
          />
        </Svg>

        {/* Content in center */}
        <View style={styles.centerContent}>
          {/* Lottie Animation - always visible */}
          <View>
            <LottieView
              animationData={liriaAnimation}
              loop
              autoPlay
              style={{
                width: LOTTIE_SIZE,
                height: LOTTIE_SIZE,
              }}
            />
          </View>

          {/* Countdown number or message below Lottie */}
          {!showEmailMessage ? (
            <Animated.View style={[styles.countdownContainer, numberAnimatedStyle]}>
              <Text style={styles.countdownText}>{currentCount}</Text>
            </Animated.View>
          ) : (
            <Animated.View style={[styles.countdownContainer, messageAnimatedStyle]}>
              <Text style={styles.emailText}>Entschuldige{dots}</Text>
            </Animated.View>
          )}
        </View>
      </Animated.View>

      {/* Detailed message card - shown when countdown completes */}
      {showEmailMessage && showDetailedMessage && (
        <Animated.View
          style={[
            styles.detailedMessageCard,
            detailedMessageAnimatedStyle,
            Platform.OS === "web" && {
              // @ts-ignore - Web-specific styles
              backdropFilter: "blur(10px)",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
            },
          ]}
        >
          {/* Red accent line on left */}
          <View style={styles.accentLine} />

          {/* Message text */}
          <Text style={styles.detailedMessageText}>
            Entschuldige, heute dauert es ein wenig länger. Dein Auftrag ist bereits bestätigt, nur
            beim Live Tracking sind unsere Server heute etwas langsamer. Weitere Details senden wir
            dir via E-Mail.
          </Text>
        </Animated.View>
      )}
    </Animated.View>
  )
}

const styles = {
  container: {
    alignItems: "center",
    justifyContent: "center",
  } as ViewStyle,

  timerCircle: {
    width: TIMER_SIZE,
    height: TIMER_SIZE,
    borderRadius: TIMER_SIZE / 2,
    backgroundColor: colors.surface,
    shadowColor: colors.palette.neutral900,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
    alignItems: "center",
    justifyContent: "center",
  } as ViewStyle,

  svg: {
    position: "absolute",
  } as ViewStyle,

  centerContent: {
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
  } as ViewStyle,

  countdownContainer: {
    marginTop: -spacing.xl,
    alignItems: "center",
    justifyContent: "center",
  } as ViewStyle,

  countdownText: {
    fontSize: COUNTDOWN_FONT_SIZE,
    fontFamily: typography.fonts.plusJakartaSans.bold,
    fontWeight: "700",
    color: colors.background,
    textAlign: "center",
  } as TextStyle,

  messageContainer: {
    alignItems: "center",
    justifyContent: "center",
    padding: spacing.xs,
  } as ViewStyle,

  emailText: {
    fontSize: 14,
    fontFamily: typography.fonts.plusJakartaSans.medium,
    fontWeight: "500",
    color: colors.background,
    textAlign: "center",
    lineHeight: 20,
  } as TextStyle,

  detailedMessageCard: {
    marginTop: spacing.lg,
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    borderRadius: 16,
    paddingVertical: 14,
    paddingHorizontal: 20,
    paddingLeft: 24,
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.3)",
    elevation: 5,
    maxWidth: 340,
    alignSelf: "center",
  } as ViewStyle,

  accentLine: {
    position: "absolute",
    left: 0,
    top: "20%",
    bottom: "20%",
    width: 3,
    backgroundColor: colors.primary,
    borderRadius: 2,
  } as ViewStyle,

  detailedMessageText: {
    color: colors.background,
    fontSize: 14,
    fontFamily: typography.fonts.plusJakartaSans.medium,
    fontWeight: "500",
    letterSpacing: 0.2,
    lineHeight: 20,
    textAlign: "left",
  } as TextStyle,
}
